/**
 * 关键词区(R2):手填 chips + 一键拓词(四组候选按组勾选)。
 * 词是表达覆盖目标不是事实依据 —— 文案与 ai KEYWORD_USAGE_RULES 同口径;
 * 勾选状态随 draft 保存,生成时只带 selected 的词。
 */
import { useState } from "react";
import { Loader2, Plus, Sparkles, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { KEYWORD_GROUP_LABEL, KEYWORD_GROUP_ORDER, type KeywordItem } from "../model/keywords";

interface Props {
  keywords: KeywordItem[];
  onChange: (next: KeywordItem[]) => void;
  /** 一键拓词(需要素材;调用方负责先落草稿再发任务)。 */
  onExpand: () => void;
  expanding: boolean;
  canExpand: boolean;
  disabled?: boolean;
}

export function KeywordZone({
  keywords,
  onChange,
  onExpand,
  expanding,
  canExpand,
  disabled,
}: Props) {
  const [input, setInput] = useState("");

  const addManual = () => {
    const word = input.trim();
    if (!word || keywords.some((k) => k.word === word)) {
      setInput("");
      return;
    }
    onChange([...keywords, { word, group: "industry", source: "manual", selected: true }]);
    setInput("");
  };

  const toggle = (word: string) =>
    onChange(keywords.map((k) => (k.word === word ? { ...k, selected: !k.selected } : k)));

  const remove = (word: string) => onChange(keywords.filter((k) => k.word !== word));

  const selectedCount = keywords.filter((k) => k.selected).length;

  return (
    <div
      className="mt-3 rounded-lg border border-border/60 bg-background/50 p-3"
      data-reporter-keywords=""
    >
      <div className="flex items-center gap-2">
        <span className="text-2xs font-semibold">GEO 关键词</span>
        <span className="text-2xs text-muted-foreground">
          表达覆盖目标,不是事实依据;生成时自然覆盖已勾选的词
        </span>
        <span className="ml-auto text-2xs text-muted-foreground">
          已选 {selectedCount}/{keywords.length}
        </span>
      </div>

      <div className="mt-2 flex items-center gap-1.5">
        <input
          value={input}
          disabled={disabled}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addManual();
            }
          }}
          placeholder="手填关键词,回车添加"
          className="w-44 rounded-md border border-border/60 bg-background px-2 py-1 text-xs"
          aria-label="手填关键词"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || !input.trim()}
          onClick={addManual}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="ml-auto text-xs"
          disabled={disabled || expanding || !canExpand}
          title={canExpand ? undefined : "先填素材再拓词(拓词以主题/素材/行业为输入)"}
          onClick={onExpand}
          data-reporter-expand-keywords=""
        >
          {expanding ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          )}
          {expanding ? "拓词中…" : "一键拓词(约 2 积分)"}
        </Button>
      </div>

      {keywords.length > 0 && (
        <div className="mt-2.5 flex flex-col gap-1.5">
          {KEYWORD_GROUP_ORDER.map((group) => {
            const items = keywords.filter((k) => k.group === group);
            if (!items.length) return null;
            return (
              <div key={group} className="flex flex-wrap items-center gap-1.5">
                <span className="w-14 shrink-0 text-2xs text-muted-foreground">
                  {KEYWORD_GROUP_LABEL[group]}
                </span>
                {items.map((k) => (
                  <span
                    key={k.word}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs transition-colors",
                      k.selected
                        ? "border-primary bg-primary/10 font-medium text-primary"
                        : "border-border text-muted-foreground"
                    )}
                  >
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => toggle(k.word)}
                      title={k.selected ? "点击取消选用" : "点击选用"}
                    >
                      {k.word}
                    </button>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => remove(k.word)}
                      aria-label={`删除关键词 ${k.word}`}
                      className="text-muted-foreground/70 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
